import { ArrowUp } from '@carbon/react/icons';
import { resourcesAtom } from 'atoms/root';
import {
  Accordion,
  AccordionItem,
  Button,
  DataTable,
  DataTableRenderProps,
  InlineNotification,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from '@carbon/react';
import classnames from 'classnames';
import { EventsPanel, EventsPanelOpenButton } from 'components/events-panel';
import { PageHeader } from 'components/page-header';
import { Section } from 'components/section';
import { ValidityStatusIndicator } from 'components/structure-grids/validity-status-indicator';
import { useAtom, useAtomValue } from 'jotai';
import { createResourceAtom } from 'lib/atoms';
import { subscribe } from 'lib/pusher';
import { DECIMAL_FORMATTER_TWO_FRACTIONS } from 'lib/ui';
import { capitalize } from 'lodash-es';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import {
  StructureGridCollectionItem,
  StructureGridCollectionResponse,
} from 'types/api-responses';
import { parseUTCdateTime } from 'utils/dates';
import { formatMutationResource } from 'utils/misc';
import { pageTitle } from 'utils/page-title';
import {
  structureGridCollectionValidityAtom,
  updateStructureGridCollectionAtom,
} from './atoms';
import { LandingAnalysisAccordion } from './landing-analysis/landing-analysis-accordion';
import { PromoteCollectionModal } from './promote-collection-modal';
import { StructureGridCollectionValidity } from './structure-grid-collection-validity';
import style from './structure-grids-collection-details.module.scss';

export const StructureGridsCollectionDetails = () => {
  const location = useLocation();

  const collectionId = location.pathname.split('/').pop();

  const collectionAtom = useMemo(
    () =>
      createResourceAtom<StructureGridCollectionResponse>(
        `/structure_grid_collections/${collectionId}`
      ),
    [collectionId]
  );

  const [collection, refetchCollection] = useAtom(collectionAtom);

  const resources = useAtomValue(resourcesAtom);

  // State for processing and validation
  const [isCollectionProcessing, setIsCollectionProcessing] =
    useState<boolean>(false);
  const [isCollectionValidating, setIsCollectionValidating] =
    useState<boolean>(false);
  const [isCollectionProcessingError, setIsCollectionProcessingError] =
    useState<string | null>(null);

  // State for promote/remove promotion modal
  const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false);
  const [promoteCollectionError, setPromoteCollectionError] = useState<
    string | null
  >(null);
  const [promoteCollectionResult, promoteCollection] = useAtom(
    updateStructureGridCollectionAtom
  );

  const collectionData =
    collection?.data && !('error' in collection.data) ? collection.data : null;

  const mappedResults = useMemo(() => {
    return (
      collectionData?.structureGridCollectionItems?.map(
        (item: StructureGridCollectionItem) => ({
          id: `${item.geologicInterval}-${item.gridSurface}`,
          originalId: item.structureGridId,
          geologicInterval: item.geologicInterval,
          gridSurface: item.gridSurface,
          validity: item.validity,
          minDepth: item.minDepth,
          maxDepth: item.maxDepth,
          avgThickness: item.avgThickness,
          source: item.source,
          createdAt: moment(item.createdAt).format('MM/DD/YYYY'),
          owner: item.owner,
          structureGridId: item.structureGridId,
        })
      ) || []
    );
  }, [collectionData?.structureGridCollectionItems]);

  const [
    structureGridCollectionValidity,
    fetchStructureGridCollectionValidity,
  ] = useAtom(structureGridCollectionValidityAtom);

  const {
    data: structureGridCollectionValidityData,
    loading: structureGridCollectionValidityLoading,
    error: structureGridCollectionValidityError,
  } = formatMutationResource(structureGridCollectionValidity);

  // Reusable function to fetch validation results
  const fetchValidationResults = useCallback(() => {
    if (collectionId) {
      fetchStructureGridCollectionValidity({
        url: `/queries/structure_grid_collections/validations`,
        type: 'POST',
        data: {
          structureGridCollections: [{ id: parseInt(collectionId) }],
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionId]);

  // Get all failed grid IDs from validation results
  const failedGridIds = useMemo(() => {
    if (!structureGridCollectionValidityData?.results) return new Set<number>();

    const failedIds = new Set<number>();
    structureGridCollectionValidityData.results.forEach((result) => {
      if (result.displayMetadata?.failedGridIds) {
        result.displayMetadata.failedGridIds.forEach((id) => failedIds.add(id));
      }
    });
    return failedIds;
  }, [structureGridCollectionValidityData]);

  // Check if any rows actually match the failed grid IDs
  const hasFailedRows = useMemo(() => {
    return mappedResults.some(
      (row) => row.originalId && failedGridIds.has(row.originalId)
    );
  }, [mappedResults, failedGridIds]);

  useEffect(() => {
    fetchValidationResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pusher effect for collection extraction
  useEffect(
    () => {
      if (!collection?.data || 'error' in collection.data) {
        return;
      }

      const data = collection.data;
      const status = data.status;
      // Only subscribe if status is 'processing' or 'created' or 'failed'
      if (
        (status === 'processing' ||
          status === 'created' ||
          status === 'failed') &&
        data.notifications?.channel
      ) {
        const { channel, events } = data.notifications;

        if (channel && events) {
          setIsCollectionProcessing(true);

          const extractionEvent = events[1].name; // extraction_status_change
          const subscription = subscribe(channel);

          subscription.bind(
            extractionEvent,
            (pusherData: {
              status: 'processed' | 'failed';
              errorMessage?: string;
            }) => {
              if (pusherData.status === 'processed') {
                setIsCollectionProcessingError(null);
                setIsCollectionProcessing(false);
                refetchCollection();
                fetchValidationResults();
              } else if (pusherData.status === 'failed') {
                setIsCollectionProcessing(false);
                setIsCollectionProcessingError(
                  'Structure Grid Collection validation did not complete. See comments for details.'
                );
              }
            }
          );

          return () => {
            subscription.unbind(extractionEvent);
          };
        }
      } else {
        setIsCollectionProcessing(false);
        setIsCollectionProcessingError(null);
      }
      return undefined;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [collection?.data]
  );

  // Pusher effect for collection validation
  useEffect(
    () => {
      if (!collection?.data || 'error' in collection.data) {
        return;
      }

      const data = collection.data;

      if (data.validity === 'incomplete' && data.notifications?.channel) {
        const { channel, events } = data.notifications;

        if (channel && events) {
          setIsCollectionValidating(true);
          const validationEvent = events[2].name; // validation_status_change
          const subscription = subscribe(channel);

          subscription.bind(
            validationEvent,
            (pusherData: {
              status: 'processed' | 'failed';
              errorMessage?: string;
            }) => {
              if (pusherData.status === 'processed') {
                setIsCollectionProcessingError(null);
                setIsCollectionValidating(false);
                refetchCollection();
                fetchValidationResults();
              } else if (pusherData.status === 'failed') {
                setIsCollectionValidating(false);
                setIsCollectionProcessingError(
                  'Structure Grid Collection validation did not complete. See comments for details.'
                );
              }
            }
          );

          return () => {
            subscription.unbind(validationEvent);
          };
        }
      } else {
        setIsCollectionProcessingError(null);
      }
      return undefined;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [collection?.data]
  );

  // Events Content component
  const EventsContent = ({
    collection,
  }: {
    collection: StructureGridCollectionResponse;
  }) => (
    <EventsPanel
      header={collection.collectionName}
      title="Structure Grid Collection"
      helperText="Structure Grid Collection context allows you to share information about a structure grid collection."
      eventsHref={collection.resources.events.href}
      commentsHref={collection.resources.comments.href}
    />
  );

  // Simple validity display component
  const CollectionValidityDisplay = () => {
    return (
      <div className={style.validityDisplay}>
        <span>Collection Validity:</span>
        {isCollectionProcessing ||
        isCollectionValidating ||
        isCollectionProcessingError ? (
          <span className={style.unknownValidityStatus}>
            Validity Unavailable
          </span>
        ) : (
          <>
            <ValidityStatusIndicator status={collectionData?.validity || ''} />
            <span>{capitalize(collectionData?.validity || '')}</span>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle('Structure Grid Collection Details')}</title>
      </Helmet>
      {collectionData && <EventsContent collection={collectionData} />}
      <div className={style.structureGridsPageHeader}>
        <PageHeader
          title={
            <h1 className={style.titleRow}>
              {collectionData?.collectionName || '--'}
              {collectionData?.isDefault && <Tag size="sm">Default</Tag>}
            </h1>
          }
          breadcrumbs={[
            ['/structure_grids', 'Structure Grid Management'],
            ['/structure_grid_collections', 'Structure Grid Collections'],
            [location.pathname, collectionData?.collectionName || '--'],
          ]}
          attributes={<CollectionValidityDisplay />}
          actions={
            <div className={style.actionsArea}>
              <EventsPanelOpenButton />
              {collectionData?.isDefault ? (
                <Button
                  kind="tertiary"
                  onClick={() => setIsPromoteModalOpen(true)}
                >
                  Remove Default
                </Button>
              ) : (
                <Button
                  renderIcon={ArrowUp}
                  disabled={collectionData?.validity === 'invalid'}
                  onClick={() => setIsPromoteModalOpen(true)}
                >
                  Promote Collection
                </Button>
              )}
            </div>
          }
        ></PageHeader>
      </div>
      {isCollectionProcessingError && (
        <div className={style.errorNotification}>
          <InlineNotification
            kind="error"
            title={isCollectionProcessingError}
            hideCloseButton
          />
        </div>
      )}
      <div className={style.mainContent}>
        <div className={style.wrapper}>
          <div className={style.rowBlock}>
            <Section header="Structure Grid Collection Details">
              <div className={style.content}>
                <div className={style.contentRow}>
                  <div className={style.contentCell}>
                    <p>Basin</p>
                    <span className={style.contentCellValue}>
                      {collectionData?.basin || '--'}
                    </span>
                  </div>
                  <div className={style.contentCell}>
                    <p>Collection Owner</p>
                    <span className={style.contentCellValue}>
                      {collectionData?.owner || '--'}
                    </span>
                  </div>
                  <div className={style.contentCell}>
                    <p>Geologic Intervals</p>
                    <span className={style.contentCellValue}>
                      {collectionData?.structureGridCollectionItems?.length ??
                        '--'}
                    </span>
                  </div>
                  <div className={style.contentCell}>
                    <p>Default</p>
                    <span className={style.contentCellValue}>
                      {collectionData !== null
                        ? collectionData.isDefault
                          ? 'Yes'
                          : 'No'
                        : '--'}
                    </span>
                  </div>
                  <div className={style.contentCell}>
                    <p>Default Start</p>
                    <span className={style.contentCellValue}>
                      {collectionData?.defaultStartTime
                        ? parseUTCdateTime(collectionData.defaultStartTime)
                        : '--'}
                    </span>
                  </div>
                  <div className={style.contentCell}>
                    <p>Default End</p>
                    <span className={style.contentCellValue}>
                      {collectionData?.defaultEndTime
                        ? parseUTCdateTime(collectionData.defaultEndTime)
                        : '--'}
                    </span>
                  </div>
                  <div className={style.contentCell}>
                    <p>Created At</p>
                    <span className={style.contentCellValue}>
                      {collectionData?.sourceCollectionCreatedAt
                        ? parseUTCdateTime(
                            collectionData.sourceCollectionCreatedAt
                          )
                        : '--'}
                    </span>
                  </div>
                </div>
              </div>
            </Section>
          </div>
        </div>
      </div>
      <div className={style.tableContainer}>
        <Accordion className={style.accordion}>
          <AccordionItem title="Landing Analysis" open>
            {collectionData && collectionData.resources.queries && (
              <LandingAnalysisAccordion
                collectionId={String(collectionData.id)}
                collectionName={collectionData.collectionName}
                initialPollingHref={
                  collectionData.hasActiveJob
                    ? collectionData.activeJobStatus?.href
                    : undefined
                }
                exportHref={
                  collectionData.resources.wellLandingAnalysisExport?.href
                }
                pusherChannel={collectionData.notifications?.channel}
                landingAutoUpdateEnabled={
                  collectionData.landingAutoUpdateEnabled
                }
                collectionValidity={collectionData.validity}
                defaultParametersHref={
                  resources?.wellLandingAnalyses?.defaultParameters?.href
                }
                queryHrefs={{
                  landingAnalysisCounts:
                    collectionData.resources.queries?.wellLandingAnalysisCounts
                      ?.href ?? '',
                  wellLandingAnalysisRequests:
                    collectionData.resources.queries
                      ?.wellLandingAnalysisRequests?.href ?? '',
                }}
              />
            )}
          </AccordionItem>
        </Accordion>
      </div>
      <div className={style.tableContainer}>
        <Accordion className={style.accordion}>
          <AccordionItem title="Content Management" open>
            {hasFailedRows && (
              <div className={style.warningNotification}>
                <InlineNotification
                  title="The highlighted grids are associated with failures during the Collection Validation checks."
                  subtitle="Please refer to the Collection Validity section below."
                  kind="warning"
                />
              </div>
            )}
            <DataTable
              headers={[
                { key: 'geologicInterval', header: 'Geologic Interval' },
                { key: 'gridSurface', header: 'Grid Surface' },
                { key: 'validity', header: 'Grid  Validity' },
                { key: 'minDepth', header: 'Min. Depth' },
                { key: 'maxDepth', header: 'Max. Depth' },
                { key: 'avgThickness', header: 'Avg. Thickness' },
                { key: 'source', header: 'Source' },
                { key: 'createdAt', header: 'Created At' },
                { key: 'owner', header: 'Owner' },
              ]}
              rows={mappedResults}
            >
              {({
                rows,
                headers,
                getTableProps,
                getHeaderProps,
                getRowProps,
              }: DataTableRenderProps<any, any[]>) => (
                <Table
                  {...getTableProps()}
                  className={style.structureGridsTable}
                >
                  <TableHead>
                    <TableRow>
                      {headers.map((header: any) => (
                        <TableHeader
                          {...getHeaderProps({ header })}
                          key={header.key}
                        >
                          {header.header}
                        </TableHeader>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row: any) => {
                      const rowData = mappedResults.find(
                        (item) => item.id === row.id
                      );
                      const isFailed =
                        rowData?.originalId &&
                        failedGridIds.has(rowData.originalId);

                      return (
                        <TableRow
                          {...getRowProps({ row })}
                          key={row.id}
                          className={classnames({
                            [style.failedRow]: isFailed,
                          })}
                        >
                          {row.cells.map((cell: any) => (
                            <TableCell key={cell.id}>
                              {cell.info.header === 'gridSurface' ? (
                                <Link
                                  href={`/structure_grids/${
                                    mappedResults.find(
                                      (item) => item.id === row.id
                                    )?.structureGridId
                                  }`}
                                >
                                  {cell.value}
                                </Link>
                              ) : cell.info.header === 'validity' ? (
                                <div className={style.validityDisplay}>
                                  <ValidityStatusIndicator
                                    status={String(cell.value)}
                                  />
                                  <span>{capitalize(String(cell.value))}</span>
                                </div>
                              ) : cell.info.header === 'avgThickness' ||
                                cell.info.header === 'minDepth' ||
                                cell.info.header === 'maxDepth' ? (
                                cell.value !== null &&
                                cell.value !== undefined ? (
                                  DECIMAL_FORMATTER_TWO_FRACTIONS.format(
                                    cell.value
                                  )
                                ) : (
                                  '--'
                                )
                              ) : (
                                cell.value
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </DataTable>
          </AccordionItem>

          <AccordionItem title="Collection Validity" open>
            {structureGridCollectionValidityError &&
              !structureGridCollectionValidityLoading && (
                <div className={style.loadingContainer}>
                  <InlineNotification
                    kind="error"
                    title="Unable to retrieve Structure Grid Collection Validation results. Please try again later"
                  />
                </div>
              )}

            {!structureGridCollectionValidityError && (
              <StructureGridCollectionValidity
                validityResults={structureGridCollectionValidityData}
                loading={structureGridCollectionValidityLoading}
              />
            )}
          </AccordionItem>
        </Accordion>
      </div>
      <PromoteCollectionModal
        isOpen={isPromoteModalOpen}
        isPromoted={collectionData?.isDefault ?? false}
        collectionName={collectionData?.collectionName ?? ''}
        basin={collectionData?.basin ?? ''}
        isLoading={promoteCollectionResult.loading}
        error={promoteCollectionError}
        onClose={() => {
          setIsPromoteModalOpen(false);
          setPromoteCollectionError(null);
        }}
        onConfirm={() => {
          if (!collectionData?.href) return;
          setPromoteCollectionError(null);
          promoteCollection({
            url: collectionData.href,
            type: 'PATCH',
            data: {
              structureGridCollection: {
                isDefault: !collectionData.isDefault,
              },
            },
            onSuccess: () => {
              setIsPromoteModalOpen(false);
              refetchCollection();
            },
            onError: (error) => {
              setPromoteCollectionError(
                error?.[0]?.message ?? 'Something went wrong. Please try again.'
              );
            },
          });
        }}
      />
    </>
  );
};
